/**
 * Investor-Portal — EBITDA-Audit (#1206 v2.584.0)
 *
 * Lebendes Werkzeug: zeigt WO Pfad A (Kostenrechnung/analytic) und Pfad B
 * (§231-UGB-Hauptbuch/move.line) auseinanderlaufen. 4 Blöcke:
 *   Block 1 — Kostenstellen ohne Klassifikation (Buchungen außerhalb Segment/Gemein/Sonstige).
 *   Block 2 — KS-lose Klasse-4-7-Konten, die nicht in der Whitelist stehen.
 *   Block 3 — Reconciliation-Status pro Monat (Ampel grün ≤100 / gelb ≤500 / rot >500).
 *   Block 4 — Hauptbuch-vs-Analytics-Divergenz pro §231-EBITDA-Konto.
 *
 * Read-only. Differenz wird NICHT versteckt.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import PeriodToggle, { buildPeriodQuery } from './components/PeriodToggle';
import DataMaturityBanner from './components/DataMaturityBanner';
import { defaultMatureMonthState, applyYtdCutoff } from '../../utils/data-maturity';

const API = import.meta.env.VITE_API_URL || '';
const MONTH_LABELS = ['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'];

function fmtEur(v) { if (v==null||isNaN(v)) return '–'; return new Intl.NumberFormat('de-AT',{style:'currency',currency:'EUR',maximumFractionDigits:2}).format(v); }
function fmtPct(v) { if (v==null||isNaN(v)) return '–'; return `${v.toFixed(2)} %`; }

const AMPEL = {
  green:  { dot: '#10B981', tint: '#D1FAE5', text: '#065F46', label: 'OK' },
  yellow: { dot: '#F59E0B', tint: '#FEF3C7', text: '#92400E', label: 'Prüfen' },
  orange: { dot: '#F59E0B', tint: '#FEF3C7', text: '#92400E', label: 'Prüfen' },
  red:    { dot: '#EF4444', tint: '#FEE2E2', text: '#991B1B', label: 'Divergenz' },
};

/** Odoo-Deeplink auf die Journal-Items eines Kontos im Zeitraum. */
function odooMoveLineLink(base, accountId, dateFrom, dateTo) {
  if (!base || !accountId) return null;
  const b = base.replace(/\/$/, '');
  const domain = encodeURIComponent(
    `[["account_id","=",${accountId}],["date",">=","${dateFrom}"],["date","<=","${dateTo}"],["parent_state","=","posted"]]`
  );
  // Legacy web-Client: Modell-Liste mit vorgefülltem Domain-Filter.
  return `${b}/web#model=account.move.line&view_type=list&domain=${domain}`;
}

/** Odoo-Deeplink auf das Beleg-Formular (account.move) einer Buchung. */
function odooMoveFormLink(base, moveId) {
  if (!base || !moveId) return null;
  const b = base.replace(/\/$/, '');
  return `${b}/web#id=${moveId}&model=account.move&view_type=form`;
}

/** Odoo-Deeplink auf das Kostenrechnungs-Posten-Formular (account.analytic.line) —
 *  für Orphan-Analytics ohne backenden Beleg (move_line_id=false). */
function odooAnalyticLineLink(base, analyticLineId) {
  if (!base || !analyticLineId) return null;
  const b = base.replace(/\/$/, '');
  return `${b}/web#id=${analyticLineId}&model=account.analytic.line&view_type=form`;
}

export default function InvestorEbitdaAudit() {
  const [searchParams] = useSearchParams();
  const [periodState, setPeriodState] = useState(() => {
    const mature = defaultMatureMonthState();
    return {
      period: searchParams.get('period') || 'month',
      year: parseInt(searchParams.get('year') || mature.year, 10),
      month: parseInt(searchParams.get('month') || mature.month, 10),
      quarter: parseInt(searchParams.get('quarter') || Math.ceil((new Date().getMonth() + 1) / 3), 10),
      fromDate: searchParams.get('from_date') || null,
      toDate: searchParams.get('to_date') || null,
    };
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const effectivePeriodState = useMemo(() => applyYtdCutoff(periodState), [periodState]);
  const periodQuery = useMemo(() => buildPeriodQuery(effectivePeriodState), [effectivePeriodState]);

  useEffect(() => {
    let abort = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const q = new URLSearchParams(periodQuery);
        const res = await fetch(`${API}/api/investor/ebitda-audit?${q.toString()}`, { credentials: 'include' });
        const json = await res.json();
        if (abort) return;
        if (!res.ok) { setError(json.error || `HTTP ${res.status}`); setData(null); } else setData(json);
      } catch (e) { if (!abort) setError(e.message); }
      finally { if (!abort) setLoading(false); }
    }
    load(); return () => { abort = true; };
  }, [periodQuery]);

  const base = data?.odoo_base_url;
  const dateFrom = data?.period?.dateFrom;
  const dateTo = data?.period?.dateTo;
  const master = AMPEL[data?.master_ampel] || AMPEL.green;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A2E]">EBITDA-Audit</h1>
          <div className="text-sm text-[#6B7280] mt-1">
            Wo laufen Kostenrechnung (Pfad A) und §231-UGB-Hauptbuch (Pfad B) auseinander?
            Buchungsfehler in Odoo korrigieren → Seite neu laden → Mängelliste schrumpft.
          </div>
        </div>
        <PeriodToggle
          period={periodState.period} year={periodState.year} month={periodState.month}
          quarter={periodState.quarter} fromDate={periodState.fromDate} toDate={periodState.toDate}
          onChange={setPeriodState}
        />
      </div>

      <DataMaturityBanner periodState={periodState} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">Fehler: {error}</div>
      )}

      {loading && !data && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center text-sm text-[#9CA3AF]">
          Lade Odoo-Daten… (~10 s — direkter Live-Read aus account.move.line + analytic.line)
        </div>
      )}

      {data && (
        <>
          {/* Master-Card */}
          <div className="rounded-xl border p-4 flex items-center gap-3"
            style={{ backgroundColor: master.tint, borderColor: master.dot }}>
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/60">
              {data.master_ampel === 'green'
                ? <CheckCircle2 width={20} height={20} stroke={master.dot} />
                : data.master_ampel === 'red'
                  ? <AlertTriangle width={20} height={20} stroke={master.dot} />
                  : <AlertCircle width={20} height={20} stroke={master.dot} />}
            </span>
            <div className="flex-1">
              <div className="text-sm font-semibold" style={{ color: master.text }}>
                {data.master_ampel === 'green' ? 'Reconciliation OK — alle Monate innerhalb Toleranz'
                  : data.master_ampel === 'red' ? 'Hauptbuch-Analytics-Divergenz (Audit prüfen)'
                  : 'Geringfügige Divergenz — Detail prüfen'}
              </div>
              <div className="text-xs mt-0.5" style={{ color: master.text }}>
                GL-Differenz {fmtEur(data.summary?.gl_differenz)} · errechnetes EBITDA {fmtEur(data.summary?.errechnetes_ebitda)} · GuV-EBITDA {fmtEur(data.summary?.guv_ebitda)}
              </div>
            </div>
          </div>

          {/* Block 3 — Reconciliation pro Monat */}
          <AuditCard title="Reconciliation pro Monat"
            sub="errechnetes EBITDA (Pfad A + GL-Bridge) ≡ GuV-EBITDA. GL-Differenz = Lücke Hauptbuch vs. Kostenrechnung."
            count={(data.block3_reconciliation || []).length} defaultOpen>
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                <tr>
                  <Th>Monat</Th><Th right>Pfad A</Th><Th right>errechnetes EBITDA</Th>
                  <Th right>GuV-EBITDA</Th><Th right>GL-Differenz</Th><Th right>Δ %</Th><Th center>Ampel</Th>
                </tr>
              </thead>
              <tbody>
                {(data.block3_reconciliation || []).map(b => {
                  const a = AMPEL[b.ampel] || AMPEL.green;
                  return (
                    <tr key={b.monat} className="border-t border-[#F3F4F6]">
                      <td className="px-3 py-2 text-[#1A1A2E]">{MONTH_LABELS[b.monat - 1] || `M${b.monat}`}</td>
                      <Td>{fmtEur(b.pfad_a)}</Td>
                      <Td bold>{fmtEur(b.errechnetes_ebitda)}</Td>
                      <Td>{fmtEur(b.guv_ebitda)}</Td>
                      <Td bold>{fmtEur(b.gl_differenz)}</Td>
                      <Td>{b.delta_pct == null ? '–' : fmtPct(b.delta_pct)}</Td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium"
                          style={{ backgroundColor: a.tint, color: a.text }}>
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: a.dot }} />{a.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </AuditCard>

          {/* Block 4 — Hauptbuch vs Analytics pro Konto (+ Line-Drill-down) */}
          <AuditCard title="Hauptbuch vs. Analytics pro Konto"
            sub={'§231-EBITDA-Konten, deren Hauptbuch-Saldo (−move.balance) vom Analytics-Wert (Σ amount) abweicht. Σ aller Δ = GL-Differenz. Klick auf eine Konto-Zeile → Drill-down auf Buchungs-Lines · „In Odoo öffnen" → Direktsprung zum Beleg.'}
            count={(data.block4_gl_divergenz?.accounts || []).length} defaultOpen>
            {(data.block4_gl_divergenz?.accounts || []).length === 0 ? (
              <EmptyRow text="Keine divergenten Konten — Hauptbuch und Kostenrechnung stimmen überein." />
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                  <tr>
                    <Th>Konto</Th><Th right>Hauptbuch</Th><Th right>Analytics</Th>
                    <Th right>Δ</Th><Th right>Lines</Th><Th center>Status</Th><Th right></Th>
                  </tr>
                </thead>
                <tbody>
                  {(data.block4_gl_divergenz.accounts).map(a => (
                    <Block4Row key={a.account_id} a={a} base={base}
                      dateFrom={dateFrom} dateTo={dateTo} periodQuery={periodQuery} />
                  ))}
                  <tr className="bg-[#F8F9FC] font-semibold border-t border-[#E5E7EB]">
                    <td className="px-3 py-2 text-[#1A1A2E]">Σ</td>
                    <Td bold>{fmtEur(data.block4_gl_divergenz.total_hauptbuch)}</Td>
                    <Td bold>{fmtEur(data.block4_gl_divergenz.total_analytics)}</Td>
                    <Td bold>{fmtEur(data.block4_gl_divergenz.total_delta)}</Td>
                    <td colSpan={3} />
                  </tr>
                </tbody>
              </table>
            )}
          </AuditCard>

          {/* Block 1 — unklassifizierte Kostenstellen */}
          <AuditCard title="Kostenstellen ohne Klassifikation"
            sub="Kostenstellen mit Buchungen, die weder einem Segment noch Gemein/Sonstige zugeordnet sind. Im Mapping-Editor zuordnen."
            count={(data.block1_unclassified_ks || []).length}>
            {(data.block1_unclassified_ks || []).length === 0 ? (
              <EmptyRow text="Alle Kostenstellen sind klassifiziert." />
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                  <tr><Th>KS-ID</Th><Th>Kostenstelle</Th><Th right>Σ Buchungen</Th><Th right>Lines</Th></tr>
                </thead>
                <tbody>
                  {(data.block1_unclassified_ks).map(k => (
                    <tr key={k.id} className="border-t border-[#F3F4F6]">
                      <td className="px-3 py-2 text-[#9CA3AF] tabular-nums">#{k.id}</td>
                      <td className="px-3 py-2 text-[#1A1A2E]">{k.name}</td>
                      <Td>{fmtEur(k.total)}</Td>
                      <Td>{k.lines}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </AuditCard>

          {/* Block 2 — KS-lose Klasse-4-7-Konten */}
          <AuditCard title="KS-lose operative Konten (Klasse 4-7)"
            sub="GuV-Buchungen ohne Kostenstelle, die nicht in der Whitelist stehen → Kandidaten für die Include-Liste im Mapping-Editor."
            count={(data.block2_ksless_accounts || []).length}>
            {(data.block2_ksless_accounts || []).length === 0 ? (
              <EmptyRow text="Keine KS-losen operativen Buchungen außerhalb der Whitelist." />
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
                  <tr><Th>Konto</Th><Th>Bezeichnung</Th><Th right>Σ Buchungen</Th><Th right>Lines</Th></tr>
                </thead>
                <tbody>
                  {(data.block2_ksless_accounts).map(a => (
                    <tr key={a.account_code} className="border-t border-[#F3F4F6]">
                      <td className="px-3 py-2 font-mono text-[#4B5563]">{a.account_code}</td>
                      <td className="px-3 py-2 text-[#1A1A2E]">{a.account_name}</td>
                      <Td>{fmtEur(a.total)}</Td>
                      <Td>{a.lines}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </AuditCard>
        </>
      )}
    </div>
  );
}

function Th({ children, right, center }) {
  return (
    <th className={`px-3 py-2 font-medium text-[#6B7280] text-xs uppercase tracking-wider ${right ? 'text-right' : center ? 'text-center' : 'text-left'}`}>
      {children}
    </th>
  );
}
function Td({ children, bold }) {
  return <td className={`px-3 py-2 text-right tabular-nums ${bold ? 'text-[#1A1A2E] font-medium' : 'text-[#4B5563]'}`}>{children}</td>;
}
function EmptyRow({ text }) {
  return <div className="px-4 py-6 text-center text-xs text-[#10B981]">✓ {text}</div>;
}

/** Eine Konto-Zeile in Block 4 — aufklappbar mit Live-Drill-down auf Buchungs-Lines. */
function Block4Row({ a, base, dateFrom, dateTo, periodQuery }) {
  const [open, setOpen] = useState(false);
  const [drill, setDrill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const am = AMPEL[a.level] || AMPEL.yellow;
  const acctLink = odooMoveLineLink(base, a.account_id, dateFrom, dateTo);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && drill === null && !loading) {
      setLoading(true); setErr(null);
      try {
        const q = new URLSearchParams(periodQuery);
        q.set('account_id', a.account_id);
        const res = await fetch(`${API}/api/investor/ebitda-audit/account-lines?${q.toString()}`, { credentials: 'include' });
        const json = await res.json();
        if (!res.ok) setErr(json.error || `HTTP ${res.status}`);
        else setDrill(json);
      } catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    }
  }

  return (
    <>
      <tr className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC] cursor-pointer" onClick={toggle}>
        <td className="px-3 py-2">
          <span className={`inline-block mr-1.5 text-[#9CA3AF] transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
          <span className="font-mono text-[#4B5563]">{a.account_code}</span>
          <span className="text-[#9CA3AF] ml-2">{a.account_name}</span>
        </td>
        <Td>{fmtEur(a.hauptbuch)}</Td>
        <Td>{fmtEur(a.analytics)}</Td>
        <Td bold>{fmtEur(a.delta)}</Td>
        <Td>{a.line_count}</Td>
        <td className="px-3 py-2 text-center">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium"
            style={{ backgroundColor: am.tint, color: am.text }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: am.dot }} />
            {a.level === 'orange' ? 'Δ > 100 €' : 'Δ < 100 €'}
          </span>
        </td>
        <td className="px-3 py-2 text-right">
          {acctLink && (
            <a href={acctLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-xs text-[#338edc] hover:underline">
              In Odoo öffnen <ExternalLink width={12} height={12} />
            </a>
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-[#FCFCFD]">
          <td colSpan={7} className="px-3 pb-3 pt-1">
            {loading && (
              <div className="flex items-center gap-2 text-xs text-[#9CA3AF] py-3 animate-pulse">
                <span className="w-3 h-3 rounded-full border-2 border-[#E5E7EB] border-t-[#338edc] animate-spin" />
                Lade Buchungs-Lines aus Odoo…
              </div>
            )}
            {err && <div className="text-xs text-red-600 py-2">Fehler: {err}</div>}
            {drill && <LineDrill drill={drill} base={base} />}
          </td>
        </tr>
      )}
    </>
  );
}

/** Innere Line-Tabelle des Block-4-Drill-downs. */
function LineDrill({ drill, base }) {
  if (!drill.lines || drill.lines.length === 0) {
    return <div className="text-xs text-[#10B981] py-2">✓ Keine Buchungs-Lines mit Δ ≠ 0.</div>;
  }
  return (
    <div className="border border-[#E5E7EB] rounded-lg overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
          <tr>
            <Th>Datum</Th><Th>Typ</Th><Th>Beleg</Th><Th>Partner / Kostenstelle / Text</Th>
            <Th right>Hauptbuch</Th><Th right>Analytics</Th><Th right>Δ</Th><Th right></Th>
          </tr>
        </thead>
        <tbody>
          {drill.lines.map((l, i) => {
            const isOrphan = l.kind === 'analytic_orphan';
            const link = isOrphan
              ? odooAnalyticLineLink(base, l.analytic_line_id)
              : odooMoveFormLink(base, l.move_id);
            const rowKey = isOrphan ? `al-${l.analytic_line_id}` : `ml-${l.move_line_id || i}`;
            return (
              <tr key={rowKey} className="border-t border-[#F3F4F6]">
                <td className="px-3 py-1.5 text-[#6B7280] whitespace-nowrap">{l.date || '–'}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  {isOrphan ? (
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#FEF3C7] text-[#92400E]">Analytics o. Beleg</span>
                  ) : (
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#EEF2FF] text-[#4338CA]">Hauptbuch</span>
                  )}
                </td>
                <td className="px-3 py-1.5 font-mono text-[#4B5563] whitespace-nowrap">{l.move_name || '–'}</td>
                <td className="px-3 py-1.5 text-[#4B5563]">{l.partner || l.kostenstelle || l.label || '–'}</td>
                <Td>{fmtEur(l.hauptbuch)}</Td>
                <Td>{fmtEur(l.analytics)}</Td>
                <Td bold>{fmtEur(l.delta)}</Td>
                <td className="px-3 py-1.5 text-right">
                  {link && (
                    <a href={link} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#338edc] hover:underline">
                      In Odoo öffnen <ExternalLink width={11} height={11} />
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {drill.truncated && (
        <div className="px-3 py-1.5 text-[11px] text-[#9CA3AF] bg-[#F8F9FC] border-t border-[#E5E7EB]">
          Top 20 nach |Δ| gezeigt · {drill.remaining} weitere Lines mit Δ ≠ 0 · Σ aller Δ = {fmtEur(drill.delta)}
        </div>
      )}
      {Math.abs(drill.analytics_unattributed || 0) > 0.01 && (
        <div className="px-3 py-1.5 text-[11px] text-[#9CA3AF] bg-[#F8F9FC] border-t border-[#E5E7EB]">
          Hinweis: {fmtEur(drill.analytics_unattributed)} Analytics ohne Beleg-Zuordnung (manuelle/periodisierte Kostenrechnungs-Buchung).
        </div>
      )}
    </div>
  );
}

function AuditCard({ title, sub, count, defaultOpen, children }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-3 text-left hover:bg-[#FAFBFC]">
        <div>
          <h2 className="text-sm font-semibold text-[#1A1A2E]">{title}</h2>
          <div className="text-[11px] text-[#9CA3AF] mt-0.5">{sub}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F8F9FC] border border-[#E5E7EB] text-[#6B7280]">{count}</span>
          <span className={`text-[#9CA3AF] transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
        </div>
      </button>
      {open && <div className="overflow-x-auto">{children}</div>}
    </div>
  );
}
